import { systemContextVariablesOptionsWithoutUsableSystemContextVariables } from 'src/components/dashboard-pages/solve-insights/cards/systemContextVariablesOptions';
import { CreateTicketFieldMappingRequest } from 'src/pages/workflow-builder-edit/types';
import { ContextVariable } from 'src/types/actionBuilderApiTypes';
import { getNonTriageContextVariables } from 'src/utils/getNonTriageConextVariables';
import { isDynamicListType } from 'src/utils/solve/dynamicContextVariableUtils';

export const filterAndSortContextVariables = (
  contextVariables: ContextVariable[],
  shouldIncludeWorkflowTagContextVariable: boolean,
  shouldIncludeDynamicListContextVariables = true,
  shouldOnlyReturnSupportedFilterableContextVariables = true,
  filterFn?: (cv: ContextVariable) => boolean,
) => {
  let filteredContextVariables = contextVariables
    .filter(
      cv =>
        shouldIncludeWorkflowTagContextVariable ||
        cv.context_variable_name !== 'Workflow Tag',
    )
    .filter(
      cv =>
        shouldIncludeDynamicListContextVariables ||
        !isDynamicListType(cv.context_variable_type),
    );

  if (shouldOnlyReturnSupportedFilterableContextVariables) {
    filteredContextVariables = filteredContextVariables.filter(
      cv =>
        cv.context_variable_type === 'EMAIL' ||
        cv.context_variable_type === 'PHONE' ||
        cv.context_variable_type === 'SHORT_TEXT' ||
        cv.context_variable_type === 'LONG_TEXT' ||
        cv.context_variable_type === 'LIST' ||
        cv.context_variable_type === 'DATE' ||
        cv.context_variable_type === 'CHECKBOX' ||
        cv.context_variable_type === 'NUMBER',
    );
  }

  return filteredContextVariables
    .filter(filterFn || (() => true))
    .sort((a, b) => {
      const textA = a.context_variable_name.toUpperCase();
      const textB = b.context_variable_name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
};

export const getAllContextVariables = ({
  contextVariablesData,
  runtimeSystemContextVariables,
  shouldIncludeCustomMetricsSystemContextVariables,
  shouldIncludeRuntimeSystemContextVariables,
  shouldIncludeSystemContextVariables,
  shouldIncludeTemplateContextVariables,
  shouldIncludeTriageContextVariables,
  systemContextVariableIdsToInclude,
  templateContextVariables,
  ticketFieldMappings,
  usableSystemContextVariables,
}: {
  contextVariablesData: ContextVariable[];
  runtimeSystemContextVariables: ContextVariable[];
  shouldIncludeCustomMetricsSystemContextVariables: boolean;
  shouldIncludeRuntimeSystemContextVariables?: boolean;
  shouldIncludeSystemContextVariables: boolean;
  shouldIncludeTemplateContextVariables: boolean;
  shouldIncludeTriageContextVariables: boolean;
  systemContextVariableIdsToInclude?: string[];
  templateContextVariables: ContextVariable[];
  ticketFieldMappings: CreateTicketFieldMappingRequest[];
  usableSystemContextVariables: ContextVariable[];
}) => {
  let systemContextVariablesToAdd: ContextVariable[] = [];

  if (shouldIncludeSystemContextVariables) {
    systemContextVariablesToAdd = [
      ...systemContextVariablesToAdd,
      ...usableSystemContextVariables,
    ];
  } else if (systemContextVariableIdsToInclude?.length) {
    const filteredcontextVariables = usableSystemContextVariables.filter(cv =>
      systemContextVariableIdsToInclude.includes(cv.context_variable_id),
    );
    systemContextVariablesToAdd = [
      ...systemContextVariablesToAdd,
      ...filteredcontextVariables,
    ];
  }

  if (shouldIncludeRuntimeSystemContextVariables) {
    systemContextVariablesToAdd = [
      ...systemContextVariablesToAdd,
      ...runtimeSystemContextVariables,
    ];
  }

  if (shouldIncludeCustomMetricsSystemContextVariables) {
    systemContextVariablesToAdd = [
      ...systemContextVariablesToAdd,
      ...systemContextVariablesOptionsWithoutUsableSystemContextVariables.map(
        cv =>
          ({
            context_variable_id: cv.value,
            context_variable_name: cv.label,
            context_variable_type: cv.type,
            is_private_context_variable: false,
            is_system_context_variable: true,
            is_universal_context_variable: true,
            is_workflow_context: false,
          }) as const satisfies ContextVariable,
      ),
    ];
  }

  let contextVariables = [...contextVariablesData];

  if (shouldIncludeTemplateContextVariables) {
    contextVariables = contextVariables.concat(templateContextVariables);
  }
  contextVariables = shouldIncludeTriageContextVariables
    ? [...contextVariables]
    : getNonTriageContextVariables(ticketFieldMappings, contextVariables);

  return [...systemContextVariablesToAdd, ...contextVariables];
};
